import { Link } from 'react-router-dom';
import { useAppConfig } from '../../../context/AppConfigContext';

const HomePage = () => {
  const { config } = useAppConfig();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {config.logoUrl ? (
                <img
                  src={config.logoUrl}
                  alt="Guston Ltd"
                  className="h-9 w-auto object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <span className="text-lg font-bold text-primary-700">Guston Ltd</span>
              )}
            </div>

            <div className="flex items-center gap-4">
              <Link
                to="/check-status"
                className="text-sm text-gray-500 hover:text-gray-800 transition-colors"
              >
                Check Status
              </Link>
              <Link
                to="/admin/login"
                className="text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 px-4 py-2 rounded-lg transition-colors"
              >
                Admin Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-12 sm:pt-20 sm:pb-16">
        <div className="max-w-3xl">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight">
            Guston Trade Portal
          </h1>
            <p className="mt-4 text-lg sm:text-xl text-gray-500 leading-relaxed">
              Register to supply or source with Guston.
            </p>
            <p className="mt-2 text-sm text-gray-400">
              The Global Powerhouse in Safety Workwear, Active Outdoor Apparel &amp; Footwear.
            </p>
          </div>
      </div>

      {/* Registration Cards */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        <div className="grid md:grid-cols-2 gap-6">

          {/* Supplier Card */}
          <Link
            to="/supplier/register"
            className="block bg-white rounded-xl border border-gray-200 p-8 hover:border-accent-300 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-accent-500 mb-5">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Supplier Registration</h2>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              Submit your company details, compliance documents, and product capabilities. Our procurement team reviews every application and responds within five working days.
            </p>
            <div className="flex items-center gap-1.5 text-sm font-medium text-accent-600">
              Register as a supplier
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </Link>

          {/* Customer Card */}
          <Link
            to="/customer/register"
            className="block bg-white rounded-xl border border-gray-200 p-8 hover:border-accent-300 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary-500 mb-5">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Customer Registration</h2>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              Open a trade account to order from Guston's workwear, outdoor apparel, and footwear ranges. Approved accounts gain access to wholesale pricing and dedicated account management.
            </p>
            <div className="flex items-center gap-1.5 text-sm font-medium text-accent-600">
              Register as a customer
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </Link>

        </div>
      </div>

      {/* Footer */}
      <footer className="bg-primary-500 text-gray-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row justify-between gap-8">
            <div className="max-w-xs">
              {config.logoUrl ? (
                <img src={config.logoUrl} alt="Guston Ltd" className="h-10 w-auto mb-3 brightness-0 invert" />
              ) : (
                <h4 className="text-sm font-bold uppercase tracking-wider text-accent-300 mb-3">Guston Ltd</h4>
              )}
              <p className="text-sm leading-relaxed">
                The Global Powerhouse in Safety Workwear, Active Outdoor Apparel &amp; Footwear.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-accent-300 mb-3">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/supplier/register" className="block text-sm hover:text-white transition-colors">Supplier Registration</Link>
                <Link to="/customer/register" className="block text-sm hover:text-white transition-colors">Customer Registration</Link>
                <Link to="/check-status" className="block text-sm hover:text-white transition-colors">Check Status</Link>
                <Link to="/admin/login" className="block text-sm hover:text-white transition-colors">Admin Login</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-accent-300 mb-3">Contact</h4>
              <p className="text-sm">
                {config.supportEmail ? (
                  <a href={`mailto:${config.supportEmail}`} className="hover:text-white transition-colors">{config.supportEmail}</a>
                ) : 'info@gustonltd.com'}
              </p>
              <p className="text-sm mt-2">
                &copy; {new Date().getFullYear()} Guston Ltd. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
